// Pure helpers for the Growth page (goals/admin-panel/DESIGN.md "Growth and
// Retention pages"): date ranges, net growth, line-chart geometry and CSV.
import type { AnalyticsSeries } from "./analytics";
import { niceMax } from "./earlyAccess";

export type Bucket = AnalyticsSeries["bucket"];

/** `YYYY-MM-DD` of a UTC day plus `days`. */
export const addDays = (day: string, days: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

/** Yesterday (UTC): the latest day the rollup can have built. */
export const latestDay = (now = new Date()) => addDays(now.toISOString().slice(0, 10), -1);

export const RANGES = [
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
  { id: "6m", label: "Last 6 months", months: 6 },
  { id: "12m", label: "Last 12 months", months: 12 },
  { id: "24m", label: "Last 24 months", months: 24 },
] as const;
export type RangeId = (typeof RANGES)[number]["id"];

/** The first day of the bucket holding `day` (a week's Monday, a month's first). */
export function bucketStart(day: string, bucket: Bucket): string {
  if (bucket === "day") return day;
  if (bucket === "month") return `${day.slice(0, 8)}01`;
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return addDays(day, -((weekday + 6) % 7));
}

/**
 * `from`–`to` for a range ending on `to`, its start moved back to the start
 * of its bucket so the first bucket is whole.
 */
export function rangeDays(range: RangeId, bucket: Bucket, to: string): { from: string; to: string } {
  const spec = RANGES.find((r) => r.id === range)!;
  let from: string;
  if ("days" in spec) from = addDays(to, 1 - spec.days);
  else {
    const date = new Date(`${to}T00:00:00Z`);
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() - spec.months + 1);
    from = date.toISOString().slice(0, 10);
  }
  return { from: bucketStart(from, bucket), to };
}

/** Sign-ups minus deletions per bucket; null where either is unbuilt. */
export function netGrowth(
  signups: readonly (number | null)[],
  deletions: readonly (number | null)[],
): (number | null)[] {
  return signups.map((value, i) => {
    const gone = deletions[i];
    return value === null || value === undefined || gone === null || gone === undefined ? null : value - gone;
  });
}

/** The values of the series whose `dim` is `value` (zeros when it has no rows but the read does). */
/** 0 for each built bucket, null for the rest (from `built`, else from any series). */
export function builtZeros(data: AnalyticsSeries): (number | null)[] {
  if (data.built) return data.built.map((built) => (built ? 0 : null));
  const any = data.series[0]?.values;
  return any ? any.map((v) => (v === null ? null : 0)) : data.buckets.map(() => null);
}

export function seriesFor(data: AnalyticsSeries | undefined, dim: string, value: string): (number | null)[] {
  if (!data) return [];
  const found = data.series.find((s) => s.dims?.[dim] === value);
  if (found) return found.values;
  // A grouped read leaves out values without rows: built buckets read 0 for them.
  return builtZeros(data);
}

export interface ChartScale {
  min: number;
  max: number;
  /** Gridline values, bottom up (0 among them). */
  ticks: number[];
}

/** A y-scale from 0 (or a nice negative) to a nice maximum over every series. */
export function chartScale(series: readonly (readonly (number | null)[])[]): ChartScale {
  const known = series.flat().filter((v): v is number => v !== null);
  const top = niceMax(Math.max(0, ...known));
  const low = Math.min(0, ...known);
  const min = low < 0 ? -niceMax(-low) : 0;
  const ticks = min < 0 ? [min, 0, top] : [0, top / 2, top];
  return { min, max: top, ticks };
}

export interface LineGeometry {
  /** Per series, one polyline `points` string per run of known values (a null breaks the line). */
  runs: string[][];
  /** Each bucket's x. */
  xs: number[];
  y: (value: number) => number;
}

/** Lines for `series` over a shared scale in a `width` × `height` plot. */
export function lineGeometry(
  series: readonly (readonly (number | null)[])[],
  scale: ChartScale,
  width: number,
  height: number,
): LineGeometry {
  const count = Math.max(0, ...series.map((s) => s.length));
  const round = (n: number) => Math.round(n * 100) / 100;
  const xs = Array.from({ length: count }, (_, i) => round(count > 1 ? (i * width) / (count - 1) : width / 2));
  const span = scale.max - scale.min || 1;
  const y = (value: number) => round(height - ((value - scale.min) / span) * height);
  const runs = series.map((values) => {
    const out: string[] = [];
    let run: string[] = [];
    values.forEach((v, i) => {
      if (v === null) {
        if (run.length) out.push(run.join(" "));
        run = [];
        return;
      }
      run.push(`${xs[i]},${y(v)}`);
    });
    if (run.length) out.push(run.join(" "));
    return out;
  });
  return { runs, xs, y };
}

/** RFC 4180 CSV: fields with a comma, quote or line break are quoted; null is empty. */
export function toCsv(rows: readonly (readonly (string | number | null)[])[]): string {
  const field = (value: string | number | null) => {
    if (value === null) return "";
    const text = String(value);
    return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
  };
  return rows.map((row) => row.map(field).join(",")).join("\r\n") + "\r\n";
}
